import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { useTranslations } from '@/hooks/useTranslations';
import { router } from '@inertiajs/react';
import { Download, Paperclip, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { userName } from '../tables/calculation-request-badges';
import type { CalculationRequest, CalculationRequestAttachment } from '../types';

interface CalculationAttachmentsPanelProps {
    request: CalculationRequest;
    canUpload?: boolean;
    canDelete?: boolean;
}

function formatSize(bytes: number | null): string {
    if (bytes === null) {
        return '';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
        size /= 1024;
        unit += 1;
    }

    return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/**
 * Files in both directions: what sales supplied with the request, and what
 * engineering produced in response. Everything is served through a
 * permission-checked download route, never a public URL.
 */
export function CalculationAttachmentsPanel({ request, canUpload = false, canDelete = false }: CalculationAttachmentsPanelProps) {
    const { t } = useTranslations();
    const attachments = request.attachments ?? [];

    const sections: Array<{ kind: 'input' | 'output'; title: string; description: string }> = [
        {
            kind: 'input',
            title: t('calculation_requests.attachments.input_title', 'From sales'),
            description: t('calculation_requests.attachments.input_description', ''),
        },
        {
            kind: 'output',
            title: t('calculation_requests.attachments.output_title', 'From engineering'),
            description: t('calculation_requests.attachments.output_description', ''),
        },
    ];

    return (
        <div className="space-y-4">
            {sections.map((section) => (
                <AttachmentSection
                    key={section.kind}
                    request={request}
                    kind={section.kind}
                    title={section.title}
                    description={section.description}
                    attachments={attachments.filter((attachment) => attachment.kind === section.kind)}
                    canUpload={canUpload}
                    canDelete={canDelete}
                />
            ))}
        </div>
    );
}

interface AttachmentSectionProps {
    request: CalculationRequest;
    kind: 'input' | 'output';
    title: string;
    description: string;
    attachments: CalculationRequestAttachment[];
    canUpload: boolean;
    canDelete: boolean;
}

function AttachmentSection({ request, kind, title, description, attachments, canUpload, canDelete }: AttachmentSectionProps) {
    const { t } = useTranslations();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    const upload = async (files: FileList | null) => {
        if (!files?.length) {
            return;
        }

        setUploading(true);
        try {
            for (const file of Array.from(files)) {
                const body = new FormData();
                body.append('attachment', file);
                body.append('kind', kind);

                const response = await fetch(`/calculation-requests/${request.id}/attachments`, {
                    method: 'POST',
                    body,
                    headers: {
                        'X-CSRF-TOKEN': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '',
                        Accept: 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error(String(response.status));
                }
            }

            router.reload({ only: ['request'] });
        } catch {
            toast.error(t('common.error', 'Something went wrong'));
        } finally {
            setUploading(false);
        }
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                <div>
                    <CardTitle className="text-base">{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </div>
                {canUpload ? (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.dwg,.dxf,.zip"
                            onChange={(event) => upload(event.target.files)}
                        />
                        <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                            <Paperclip className="mr-2 h-4 w-4" />
                            {uploading
                                ? t('calculation_requests.attachments.uploading', 'Uploading...')
                                : t('calculation_requests.attachments.upload', 'Upload files')}
                        </Button>
                    </>
                ) : null}
            </CardHeader>
            <CardContent>
                {attachments.length === 0 ? (
                    <p className="text-muted-foreground text-sm">{t('calculation_requests.attachments.empty', 'No files yet.')}</p>
                ) : (
                    <ul className="space-y-2">
                        {attachments.map((attachment) => (
                            <li key={attachment.id} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium">{attachment.file_name}</p>
                                    <p className="text-muted-foreground text-xs">
                                        {[
                                            formatSize(attachment.file_size),
                                            attachment.uploaded_by
                                                ? t('calculation_requests.attachments.uploaded_by', 'Uploaded by :name').replace(
                                                      ':name',
                                                      userName(attachment.uploaded_by),
                                                  )
                                                : null,
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </p>
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                    <Button asChild variant="ghost" size="sm">
                                        <a href={attachment.download_url}>
                                            <Download className="h-4 w-4" />
                                        </a>
                                    </Button>
                                    {canDelete ? (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                if (
                                                    !window.confirm(
                                                        t('calculation_requests.attachments.delete_confirm', 'Delete :name?').replace(
                                                            ':name',
                                                            attachment.file_name,
                                                        ),
                                                    )
                                                ) {
                                                    return;
                                                }

                                                router.delete(`/calculation-requests/${request.id}/attachments/${attachment.id}`, {
                                                    preserveScroll: true,
                                                });
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    ) : null}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
}
